import React, { useEffect, useState } from 'react';
import { Clock, ChevronLeft, ChevronRight, X, ArrowUpDown, ArrowLeft, Check, Pencil, Trash2, Info } from 'lucide-react';
interface ActivityEntry {
  action: string;
  user: string;
  timestamp: string;
}
interface Webhook {
  id: string;
  name: string;
  url: string;
  type: string;
  status: 'Active' | 'Inactive';
  events: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  activity: ActivityEntry[];
}
const AVAILABLE_EVENTS = ['Client tokenisation status', 'Transaction change', 'Tokenisation status', 'Payment status', 'Refund status', 'Dispute status', 'Settlement status'];
function formatDate(iso: string) {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  const secs = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}:${secs}`;
}
/* ── Centralised style tokens (mapped from theme/tokens.css) ── */
const styles = {
  // Buttons
  btnPrimary: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]',
  btnDanger: 'text-white rounded-md font-medium text-sm transition-all bg-[var(--colors-error-default)] hover:bg-[var(--colors-error-dark)] focus:outline-none focus:ring-2 focus:ring-red-300',
  btnGhost: 'text-sm text-[var(--colors-text-secondary)] bg-[var(--colors-background-card)] border border-[var(--colors-border-default)] rounded-md hover:bg-[var(--colors-neutral-50)] transition-colors',
  // Cards
  card: 'bg-[var(--colors-background-card)] border border-[var(--colors-border-light)] rounded-lg shadow-[var(--shadows-sm)]',
  // Text
  heading: 'text-[var(--colors-text-primary)] font-semibold',
  subtext: 'text-sm text-[var(--colors-text-tertiary)]',
  label: 'block text-sm font-medium text-[var(--colors-text-secondary)] mb-1',
  // Inputs
  input: 'w-full border border-[var(--colors-border-default)] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--colors-border-focus)] bg-[var(--colors-background-card)]',
  // Badges
  badgeActive: 'bg-[var(--colors-success-light)] text-[var(--colors-success-dark)]',
  badgeInactive: 'bg-[var(--colors-neutral-100)] text-[var(--colors-neutral-600)]',
  badgeTypeGlobal: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)]',
  badgeTypeOther: 'bg-[var(--colors-primary-100)] text-[var(--colors-primary-800)]',
  badgeBase: 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium',
  // Table
  th: 'px-6 py-3 font-medium text-[var(--colors-text-secondary)] cursor-pointer hover:bg-[var(--colors-neutral-50)]',
  // Misc
  eventChip: 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-[var(--colors-neutral-100)] text-[var(--colors-text-secondary)] border border-[var(--colors-border-light)]',
  overlay: 'fixed inset-0 bg-[var(--colors-background-overlay)] flex items-center justify-center z-[var(--zindex-modal)]'
} as const;
type ViewMode = 'list' | 'add' | 'edit';
export function WebhookEndpoints() {
  const [globalWebhookUrl, setGlobalWebhookUrl] = useState('');
  const [globalUpdateStatus, setGlobalUpdateStatus] = useState<'idle' | 'success'>('idle');
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null);
  const [deleteModalWebhook, setDeleteModalWebhook] = useState<Webhook | null>(null);
  const [infoModalWebhook, setInfoModalWebhook] = useState<Webhook | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'Global',
    events: [] as string[]
  });
  useEffect(() => {
    if (globalUpdateStatus === 'success') {
      const timer = setTimeout(() => setGlobalUpdateStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
  }, [globalUpdateStatus]);
  const handleGlobalUpdate = () => {
    setGlobalUpdateStatus('success');
  };
  const handleGlobalRemove = () => {
    if (window.confirm('Are you sure you want to remove the global webhook?')) {
      setGlobalWebhookUrl('');
    }
  };
  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      type: 'Global',
      events: []
    });
  };
  const openAddView = () => {
    resetForm();
    setViewMode('add');
  };
  const openEditView = (webhook: Webhook) => {
    setFormData({
      name: webhook.name,
      url: webhook.url,
      type: webhook.type,
      events: [...webhook.events]
    });
    setEditingWebhookId(webhook.id);
    setViewMode('edit');
  };
  const handleAddWebhook = () => {
    if (!formData.name || !formData.url || formData.events.length === 0) return;
    const now = new Date().toISOString();
    const newWebhook: Webhook = {
      id: Math.random().toString(36).substring(2, 18),
      name: formData.name,
      url: formData.url,
      type: formData.type,
      status: 'Active',
      events: formData.events,
      createdAt: now,
      updatedAt: now,
      createdBy: 'admin@cimb.com.my',
      activity: [{
        action: 'Webhook created',
        user: 'admin@cimb.com.my',
        timestamp: now
      }]
    };
    setWebhooks([...webhooks, newWebhook]);
    resetForm();
    setViewMode('list');
  };
  const handleSaveEdit = () => {
    if (!formData.name || !formData.url || formData.events.length === 0) return;
    const now = new Date().toISOString();
    setWebhooks(webhooks.map(wh => wh.id === editingWebhookId ? {
      ...wh,
      name: formData.name,
      url: formData.url,
      type: formData.type,
      events: formData.events,
      updatedAt: now,
      activity: [...wh.activity, {
        action: 'Webhook updated',
        user: 'admin@cimb.com.my',
        timestamp: now
      }]
    } : wh));
    setEditingWebhookId(null);
    resetForm();
    setViewMode('list');
  };
  const handleDeleteWebhook = (webhook: Webhook) => {
    setWebhooks(webhooks.filter(wh => wh.id !== webhook.id));
    setDeleteModalWebhook(null);
  };
  const toggleEvent = (event: string) => {
    if (formData.events.includes(event)) {
      setFormData({
        ...formData,
        events: formData.events.filter(e => e !== event)
      });
    } else {
      setFormData({
        ...formData,
        events: [...formData.events, event]
      });
    }
  };
  const selectAllEvents = () => {
    setFormData({
      ...formData,
      events: [...AVAILABLE_EVENTS]
    });
  };
  const clearAllEvents = () => {
    setFormData({
      ...formData,
      events: []
    });
  };
  const statusBadge = (status: string) => status === 'Active' ? styles.badgeActive : styles.badgeInactive;
  const typeBadge = (type: string) => type === 'Global' ? styles.badgeTypeGlobal : styles.badgeTypeOther;
  // ─── Add / Edit View ───
  if (viewMode === 'add' || viewMode === 'edit') {
    const isEdit = viewMode === 'edit';
    return <div className="max-w-5xl mx-auto p-6 space-y-6" data-id="element-5528" data-mp="23pxbn4z50sby">
        <div className={`${styles.card} p-4 flex items-center gap-3`} data-id="element-5529" data-mp="23ocfxc1373ci">
          <button onClick={() => {
          setViewMode('list');
          resetForm();
          setEditingWebhookId(null);
        }} className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-primary)] transition-colors rounded-md hover:bg-[var(--colors-neutral-100)]" data-id="element-5530" data-mp="1yq2l52j9y4rf">
            <ArrowLeft className="w-5 h-5" data-id="element-5531" data-mp="3p15oa10d3tt2" />
          </button>
          <h1 className={`text-lg font-medium ${styles.heading}`} data-id="element-5532" data-mp="c7oyktv1jm1a">
            {isEdit ? 'Edit Webhook' : 'Add Webhook'}
          </h1>
        </div>

        <div className={styles.card} data-id="element-5533" data-mp="ga23xh5vlhxj">
          <div className="p-6 space-y-6" data-id="element-5534" data-mp="2hm1oikstnxo">
            <div data-id="element-5535" data-mp="2eq816fzhlf1">
              <label className={styles.label} data-id="element-5536" data-mp="7fxff4e7sil6">
                Name{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5537" data-mp="7e2pfbo360a8">*</span>
              </label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className={styles.input} placeholder="e.g, Payment Notifications" data-id="element-5538" data-mp="3ompru6sdd9e4" />
            </div>

            <div data-id="element-5539" data-mp="1dwr73ucbe0kz">
              <label className={styles.label} data-id="element-5540" data-mp="1du550a870da5">
                Webhook URL{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5541" data-mp="1iuavvo6n1vr6">*</span>
              </label>
              <input type="url" value={formData.url} onChange={e => setFormData({
              ...formData,
              url: e.target.value
            })} className={styles.input} placeholder="https://example.com/webhook" data-id="element-5542" data-mp="1sz9618n7zh09" />
            </div>

            <div data-id="element-5543" data-mp="2bnnxty5d2z24">
              <label className={styles.label} data-id="element-5544" data-mp="2brbsx1qgftiy">
                Type{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5545" data-mp="26pw91d00jt25">*</span>
              </label>
              <select value={formData.type} onChange={e => setFormData({
              ...formData,
              type: e.target.value
            })} className={styles.input} data-id="element-5546" data-mp="2kpnhky7rgh8r">
                <option value="Global" data-id="element-5547" data-mp="mc5axd9bal0c">Global</option>
                <option value="Transaction-level" data-id="element-5548" data-mp="1thxtjynuyaf4">Transaction-level</option>
                <option value="Payment" data-id="element-5549" data-mp="1tf3cmh41z7qg">Payment</option>
                <option value="Refund" data-id="element-5550" data-mp="1pf1jiwcdvmms">Refund</option>
                <option value="Dispute" data-id="element-5551" data-mp="1kghuuo6enwqq">Dispute</option>
                <option value="All" data-id="element-5552" data-mp="1kh9ovukctmew">All</option>
              </select>
            </div>

            <div data-id="element-5553" data-mp="1fiyffkxz1dx7">
              <label className={styles.label} data-id="element-5554" data-mp="1fjhnm9xtkny1">
                Events{' '}
                <span className="text-[var(--colors-error-default)]" data-id="element-5555" data-mp="2d43yfwfmv1dl">*</span>
              </label>
              <div className="border border-[var(--colors-border-default)] rounded-md p-1.5 min-h-[42px] flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-[var(--brand-primaryring)] focus-within:border-[var(--colors-border-focus)] relative bg-[var(--colors-background-card)] pr-8" data-id="element-5556" data-mp="287nap3ccip11">
                {formData.events.map(event => <span key={event} className={`${styles.eventChip} gap-1`} data-id="element-5557" data-mp="2r5gee7usn2zh">
                    {event}
                    <button onClick={() => toggleEvent(event)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] focus:outline-none" data-id="element-5558" data-mp="2ygl1rnzp07ni">
                      <X className="w-3.5 h-3.5" data-id="element-5559" data-mp="38dpktey8rn03" />
                    </button>
                  </span>)}
                <select className="flex-1 min-w-[150px] border-none focus:ring-0 text-sm py-1 bg-transparent text-[var(--colors-text-tertiary)] outline-none" onChange={e => {
                if (e.target.value) {
                  toggleEvent(e.target.value);
                  e.target.value = '';
                }
              }} value="" data-id="element-5560" data-mp="3dbiskcf70p2h">
                  <option value="" disabled data-id="element-5561" data-mp="4y23r2gf92bo">
                    -- Select one --
                  </option>
                  {AVAILABLE_EVENTS.filter(e => !formData.events.includes(e)).map(event => <option key={event} value={event} data-id="element-5562" data-mp="260jcknbmvxgw">
                      {event}
                    </option>)}
                </select>
                {formData.events.length > 0 && <button onClick={clearAllEvents} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-5563" data-mp="2etmq0vma4abj">
                    <X className="w-4 h-4" data-id="element-5564" data-mp="2jrxz344r2gsp" />
                  </button>}
              </div>
              <div className="mt-2" data-id="element-5565" data-mp="2xrpgleuh6fbg">
                <button onClick={selectAllEvents} className={`${styles.btnGhost} px-3 py-1.5`} data-id="element-5566" data-mp="2xqnywriyeukr">
                  Select all
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[var(--colors-border-light)] flex justify-end" data-id="element-5567" data-mp="zc306vbg4puu">
            <button onClick={isEdit ? handleSaveEdit : handleAddWebhook} disabled={!formData.name || !formData.url || formData.events.length === 0} className={`px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${isEdit ? styles.btnDanger : styles.btnPrimary}`} data-id="element-5568" data-mp="p74k6u86oadu">
              {isEdit ? 'Save changes' : 'Add webhook'}
            </button>
          </div>
        </div>
      </div>;
  }
  // ─── List View ───
  return <div className="max-w-7xl mx-auto p-6 space-y-6" data-id="element-5569" data-mp="ajt6omsfygb3">
      {/* Header */}
      <div className={`${styles.card} p-6 flex justify-between items-center`} data-id="element-5570" data-mp="3somrn0k1nfdq">
        <div data-id="element-5571" data-mp="1i22xnhgojjv">
          <h1 className={`text-xl ${styles.heading}`} data-id="element-5572" data-mp="1hs91otaps8w">Webhooks</h1>
          <p className={`${styles.subtext} mt-1`} data-id="element-5573" data-mp="1k66237jdej3">
            Manage webhook endpoints for receiving real-time event notifications
          </p>
        </div>
        <button onClick={openAddView} className={`${styles.btnPrimary} px-4 py-2`} data-id="element-5574" data-mp="1vv1qc2s32i1p">
          Add Webhook
        </button>
      </div>

      {/* Global Webhook */}
      <div className={styles.card} data-id="element-5575" data-mp="3mzv22r27vtll">
        <div className="p-6 border-b border-[var(--colors-border-light)]" data-id="element-5576" data-mp="3n0xskxvmnu0z">
          <h2 className={`text-base font-medium ${styles.heading}`} data-id="element-5577" data-mp="3hzqmd394v2wf">
            Global Webhook
          </h2>
          <p className={`${styles.subtext} mt-1`} data-id="element-5578" data-mp="3dywxo7nv9k2h">
            Configure a global webhook URL to receive events for all
            transactions.
          </p>
          <div className="mt-6" data-id="element-5579" data-mp="34950r6saoeuu">
            <label className={styles.label} data-id="element-5580" data-mp="3482c75ig2oi3">Webhook URL</label>
            <input type="text" value={globalWebhookUrl} onChange={e => setGlobalWebhookUrl(e.target.value)} placeholder="https://example.com/webhook" className={styles.input} data-id="element-5581" data-mp="2za0uop0o2x8q" />
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end gap-3 rounded-b-lg" data-id="element-5582" data-mp="2lb161hd5mick">
          <button onClick={handleGlobalUpdate} className={`${styles.btnPrimary} px-4 py-2 min-w-[80px] flex justify-center items-center`} data-id="element-5583" data-mp="2lcvomp0ydydh">
            {globalUpdateStatus === 'success' ? <span className="flex items-center gap-1" data-id="element-5584" data-mp="19toibio17rhu">
                <Check className="w-4 h-4" data-id="element-5585" data-mp="19u7x4qidxzrn" /> Updated!
              </span> : 'Update'}
          </button>
          <button onClick={handleGlobalRemove} className={`${styles.btnDanger} px-4 py-2`} data-id="element-5586" data-mp="i2sf79rrx17r">
            Remove
          </button>
        </div>
      </div>

      {/* Configured Webhooks */}
      <div className={styles.card} data-id="element-5587" data-mp="r1vwc87dr6jm">
        <div className="p-6 border-b border-[var(--colors-border-light)]" data-id="element-5588" data-mp="r0ubxd4h57p8">
          <h2 className={`text-base font-medium ${styles.heading}`} data-id="element-5589" data-mp="vv8b7qm9byz6">
            Configured Webhooks
          </h2>
        </div>

        <div className="overflow-x-auto" data-id="element-5590" data-mp="3uhta0r4r2wmp">
          <table className="w-full text-left text-sm text-[var(--colors-text-secondary)]" data-id="element-5591" data-mp="3ckxiw9lbm84">
            <thead className="border-b border-[var(--colors-border-light)]" data-id="element-5592" data-mp="16ngtct30ea67">
              <tr data-id="element-5593" data-mp="11pfao99hwcv9">
                <th className={styles.th} data-id="element-5594" data-mp="11pytds6yz5ac">
                  <div className="flex items-center gap-2" data-id="element-5595" data-mp="11ox7stsbv0ed">
                    Name & URL{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5596" data-mp="1ffsgo62tthhy" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-5597" data-mp="npe72m1uirc3">
                  <div className="flex items-center gap-2" data-id="element-5598" data-mp="nq77z6e4immb">
                    Type{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5599" data-mp="ircqq8vt164b" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-5600" data-mp="wrdpnisie3h5">
                  <div className="flex items-center gap-2" data-id="element-5601" data-mp="rqho3bvmqtew">
                    Status{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5602" data-mp="284zipf1hop7y" />
                  </div>
                </th>
                <th className={styles.th} data-id="element-5603" data-mp="235d1kuijiotv">
                  <div className="flex items-center gap-2" data-id="element-5604" data-mp="238ipi6ffnko0">
                    Events{' '}
                    <ArrowUpDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-5605" data-mp="2bpjf3gsvd8dw" />
                  </div>
                </th>
                <th className="px-6 py-3 font-medium text-[var(--colors-text-secondary)] text-right" data-id="element-5606" data-mp="21pir3hk2243d">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5607" data-mp="2udws866fxgj5">
              {webhooks.length === 0 ? <tr data-id="element-5608" data-mp="2ueg7tg8mbsu0">
                  <td colSpan={5} className="px-6 py-24 text-center" data-id="element-5609" data-mp="2zcrkdyqashfx">
                    <div className="flex flex-col items-center justify-center" data-id="element-5610" data-mp="2zby8ykge93rg">
                      <div className="w-12 h-12 bg-[var(--colors-neutral-50)] rounded-full flex items-center justify-center mb-4" data-id="element-5611" data-mp="2kn25z1t72gxp">
                        <Clock className="w-6 h-6 text-[var(--colors-text-tertiary)]" data-id="element-5612" data-mp="2pkv7vingsos6" />
                      </div>
                      <h3 className={`text-base font-medium ${styles.heading} mb-1`} data-id="element-5613" data-mp="3i38wqsqtp0f4">
                        No webhooks
                      </h3>
                      <p className={styles.subtext} data-id="element-5614" data-mp="1da9ijv777132">
                        Webhooks you add will appear here.
                      </p>
                    </div>
                  </td>
                </tr> : webhooks.map(webhook => <tr key={webhook.id} className="border-b border-[var(--colors-neutral-100)] last:border-0 hover:bg-[var(--colors-neutral-50)]" data-id="element-5615" data-mp="14bfr0j8q50wd">
                    <td className="px-6 py-4" data-id="element-5616" data-mp="1w1k9gccxbduy">
                      <div className="font-medium text-[var(--colors-text-primary)]" data-id="element-5617" data-mp="1ryns0oz4dnsl">
                        {webhook.name || '—'}
                      </div>
                      <div className="text-[var(--colors-text-tertiary)] text-xs mt-0.5 font-mono" data-id="element-5618" data-mp="1n26ytgd2cj8n">
                        {webhook.url}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-5619" data-mp="bn0d601bwomm">
                      <span className={`${styles.badgeBase} ${typeBadge(webhook.type)}`} data-id="element-5620" data-mp="2n8spp5cvhzk5">
                        {webhook.type}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-5621" data-mp="2w85u3jm53xy0">
                      <span className={`${styles.badgeBase} ${statusBadge(webhook.status)}`} data-id="element-5622" data-mp="30znvu9vzm2k1">
                        {webhook.status}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-5623" data-mp="3awsj6d1kk42f">
                      <div className="flex flex-wrap gap-1" data-id="element-5624" data-mp="3azofjwxrqkor">
                        {webhook.events.map(event => <span key={event} className={styles.eventChip} data-id="element-5625" data-mp="3jy9tfesapbj7">
                            {event}
                          </span>)}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-5626" data-mp="1pjqu1l31vo5e">
                      <div className="flex items-center justify-end gap-1" data-id="element-5627" data-mp="1pmldb9abv2x8">
                        <button onClick={() => openEditView(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] transition-colors" title="Edit" data-id="element-5628" data-mp="19fd7e74cq6jf">
                          <Pencil className="w-4 h-4" data-id="element-5629" data-mp="1t21gbnp16g0d" />
                        </button>
                        <button onClick={() => setDeleteModalWebhook(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-error-light)] text-[var(--colors-error-default)] hover:text-[var(--colors-error-dark)] transition-colors" title="Delete" data-id="element-5630" data-mp="1t446q8u3ib5m">
                          <Trash2 className="w-4 h-4" data-id="element-5631" data-mp="7pq846fjgnx0" />
                        </button>
                        <button onClick={() => setInfoModalWebhook(webhook)} className="p-1.5 rounded hover:bg-[var(--colors-info-light)] text-[var(--colors-info-default)] hover:text-[var(--colors-info-dark)] transition-colors" title="Details" data-id="element-5632" data-mp="cokqj79lgagl">
                          <Info className="w-4 h-4" data-id="element-5633" data-mp="3rh8mf6nix3do" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-[var(--colors-border-light)] bg-[var(--colors-neutral-50)] flex items-center justify-between rounded-b-lg" data-id="element-5634" data-mp="j3i80a7mdbhp">
          <div className="text-sm text-[var(--colors-text-tertiary)] font-medium" data-id="element-5635" data-mp="e6i1jy6uy6q2">
            {webhooks.length === 0 ? 'No entries' : `Showing 1-${webhooks.length} of ${webhooks.length} ${webhooks.length === 1 ? 'entry' : 'entries'}`}
          </div>
          <div className="flex items-center gap-4" data-id="element-5636" data-mp="2tui1gkh7wupe">
            <div className="flex items-center gap-2" data-id="element-5637" data-mp="1lvucdl0ile77">
              <span className="text-sm text-[var(--colors-text-tertiary)] font-medium" data-id="element-5638" data-mp="1lu9izypxs1ax">
                Show
              </span>
              <select className="border border-[var(--colors-border-default)] rounded-md text-sm py-1.5 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)] bg-[var(--colors-background-card)]" data-id="element-5639" data-mp="1byg8slzowjwb">
                <option data-id="element-5640" data-mp="1284xgedof8fe">25</option>
                <option data-id="element-5641" data-mp="1273k2396u9ui">50</option>
                <option data-id="element-5642" data-mp="16yuzpiqt2v5v">100</option>
              </select>
            </div>
            <div className="flex items-center gap-1" data-id="element-5643" data-mp="1717renmd8jw4">
              <button className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] disabled:opacity-50" disabled data-id="element-5644" data-mp="t8rkhbqcg1px">
                <ChevronLeft className="w-4 h-4" data-id="element-5645" data-mp="j991oym66lzg" />
              </button>
              {webhooks.length > 0 && <span className="text-sm font-medium text-[var(--colors-text-secondary)] px-2" data-id="element-5646" data-mp="o7akrjjlilax">
                  1
                </span>}
              <button className="p-1 text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)] disabled:opacity-50" disabled data-id="element-5647" data-mp="l8dquks5tmg">
                <ChevronRight className="w-4 h-4" data-id="element-5648" data-mp="9lwongevndv4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalWebhook && <div className={styles.overlay} data-id="element-5649" data-mp="jk2prvsv0fyx">
          <div className="bg-[var(--colors-background-card)] rounded-lg shadow-[var(--shadows-xl)] w-full max-w-md overflow-hidden" data-id="element-5650" data-mp="jjb5vi8mk6oh">
            <div className="flex justify-end p-4 pb-0" data-id="element-5651" data-mp="xdllna2n8qie">
              <button onClick={() => setDeleteModalWebhook(null)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-5652" data-mp="sdfqvl12unw3">
                <X className="w-5 h-5" data-id="element-5653" data-mp="12bn90773rbzo" />
              </button>
            </div>
            <div className="px-6 pb-6 pt-2 text-center" data-id="element-5654" data-mp="2vtug1z75xwvd">
              <h3 className={`text-xl ${styles.heading} mb-2`} data-id="element-5655" data-mp="2vr88w0o6pjdo">
                Delete Webhook
              </h3>
              <p className={`${styles.subtext} leading-relaxed`} data-id="element-5656" data-mp="35j4ahlphbrmn">
                Are you sure you want to delete Webhook?
                <br data-id="element-5657" data-mp="3aiqr854inq07" />
                This action cannot be undone.
              </p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-center gap-4" data-id="element-5658" data-mp="2cv8gioraodtk">
              <button onClick={() => setDeleteModalWebhook(null)} className="text-sm font-medium text-[var(--colors-text-secondary)] hover:text-[var(--colors-text-primary)] transition-colors" data-id="element-5659" data-mp="2ht9yf6czi73g">
                No, go back
              </button>
              <button onClick={() => handleDeleteWebhook(deleteModalWebhook)} className={`${styles.btnDanger} px-4 py-2`} data-id="element-5660" data-mp="22dz0rb4omdk">
                Yes, delete Webhook
              </button>
            </div>
          </div>
        </div>}

      {/* Info / Detail Modal */}
      {infoModalWebhook && <div className={styles.overlay} data-id="element-5661" data-mp="1mlr9gsxuygvp">
          <div className="bg-[var(--colors-background-card)] rounded-lg shadow-[var(--shadows-xl)] w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" data-id="element-5662" data-mp="1mmslp0qm19f0">
            <div className="flex justify-end p-4 pb-0 flex-shrink-0" data-id="element-5663" data-mp="1cttwbgnaheuc">
              <button onClick={() => setInfoModalWebhook(null)} className="text-[var(--colors-text-tertiary)] hover:text-[var(--colors-text-secondary)]" data-id="element-5664" data-mp="17w1vfx4o2ie0">
                <X className="w-5 h-5" data-id="element-5665" data-mp="yu2bavycuy6u" />
              </button>
            </div>
            <div className="px-6 pb-6 overflow-y-auto" data-id="element-5666" data-mp="tw0r27p70sts">
              <table className="w-full text-sm" data-id="element-5667" data-mp="txlms0n8uht8">
                <tbody className="divide-y divide-[var(--colors-neutral-100)]" data-id="element-5668" data-mp="2t9eoubow2dct">
                  <tr data-id="element-5669" data-mp="2o9s99vll8l0u">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top w-36" data-id="element-5670" data-mp="2o9a2w657oqf7">
                      Webhook ID
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)] font-mono text-xs" data-id="element-5671" data-mp="rb2umy2a3kr6">
                      {infoModalWebhook.id}
                    </td>
                  </tr>
                  <tr data-id="element-5672" data-mp="hlj9flr3fet5">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5673" data-mp="hl02f02zeatw">
                      Name
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-5674" data-mp="1aa6y7rb1wmbg">
                      {infoModalWebhook.name || '—'}
                    </td>
                  </tr>
                  <tr data-id="element-5675" data-mp="vf0s74bygni4">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5676" data-mp="10c0wpat5pi74">
                      Endpoint URL
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)] break-all" data-id="element-5677" data-mp="3g59m8qaxcjl5">
                      {infoModalWebhook.url}
                    </td>
                  </tr>
                  <tr data-id="element-5678" data-mp="233271dierrip">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5679" data-mp="236g72f3lnomz">
                      Type
                    </td>
                    <td className="py-3" data-id="element-5680" data-mp="2c4rxag0k0kew">
                      <span className={`${styles.badgeBase} ${typeBadge(infoModalWebhook.type)}`} data-id="element-5681" data-mp="283zkxdwdxzee">
                        {infoModalWebhook.type}
                      </span>
                    </td>
                  </tr>
                  <tr data-id="element-5682" data-mp="1fcy1v1pknjpl">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5683" data-mp="1t6fncs1tj6i9">
                      Status
                    </td>
                    <td className="py-3" data-id="element-5684" data-mp="1odmg9xivnm30">
                      <span className={`${styles.badgeBase} ${statusBadge(infoModalWebhook.status)}`} data-id="element-5685" data-mp="10pcyr7sp5kho">
                        {infoModalWebhook.status}
                      </span>
                    </td>
                  </tr>
                  <tr data-id="element-5686" data-mp="1qif5vpiqi4rx">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5687" data-mp="1qhn765tubqxf">
                      Events
                    </td>
                    <td className="py-3" data-id="element-5688" data-mp="1zj3eu5t9texy">
                      <p className="text-[var(--colors-text-tertiary)] text-xs mb-2" data-id="element-5689" data-mp="1zjwhovp3zkat">
                        This webhook receives all event types.
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-5690" data-mp="29grglfpanmpm">
                        {infoModalWebhook.events.map(event => <span key={event} className={styles.eventChip} data-id="element-5691" data-mp="2e8j6rp4jfqf8">
                            {event}
                          </span>)}
                      </div>
                    </td>
                  </tr>
                  <tr data-id="element-5692" data-mp="2s7savqfgyu2u">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5693" data-mp="2s790qvw299zd">
                      Created
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-5694" data-mp="2xcmd4997ksxb">
                      {formatDate(infoModalWebhook.createdAt)}
                    </td>
                  </tr>
                  <tr data-id="element-5695" data-mp="2sfcouwxk2nb2">
                    <td className="py-3 pr-6 font-medium text-[var(--colors-text-secondary)] whitespace-nowrap align-top" data-id="element-5696" data-mp="2oc7rf8sc1bta">
                      Last updated
                    </td>
                    <td className="py-3 text-[var(--colors-text-primary)]" data-id="element-5697" data-mp="3g341k1n0d2cv">
                      {formatDate(infoModalWebhook.updatedAt)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Activity Log */}
              <div className="mt-6 border-t border-[var(--colors-border-light)] pt-4" data-id="element-5698" data-mp="37ajz0q3lidmr">
                <h4 className={`text-sm ${styles.heading} mb-3`} data-id="element-5699" data-mp="7o3syr3dgmfj">
                  Activity Log
                </h4>
                {infoModalWebhook.activity.length === 0 ? <p className={styles.subtext} data-id="element-5700" data-mp="14miqelp7wc7z">No activity recorded.</p> : <div className="space-y-0" data-id="element-5701" data-mp="zn672axk1na5">
                    {infoModalWebhook.activity.map((entry, idx) => <div key={idx} className="flex items-start gap-3 py-2.5 relative" data-id="element-5702" data-mp="1ekeyf4muyr1v" data-mpi={idx}>
                        {idx < infoModalWebhook.activity.length - 1 && <div className="absolute left-[7px] top-[22px] bottom-0 w-px bg-[var(--colors-border-light)]" data-id="element-5703" data-mp="1nhz1mj4a6xf4" />}
                        <div className="w-[15px] h-[15px] rounded-full bg-[var(--colors-primary-100)] border-2 border-[var(--brand-primary)] flex-shrink-0 mt-0.5 relative z-10" data-id="element-5704" data-mp="1x93gzjxvckah" />
                        <div className="flex-1 min-w-0" data-id="element-5705" data-mp="26abew0wh3j41">
                          <p className="text-sm text-[var(--colors-text-primary)] font-medium" data-id="element-5706" data-mp="26b32opjtwcjr">
                            {entry.action}
                          </p>
                          <p className="text-xs text-[var(--colors-text-tertiary)] mt-0.5" data-id="element-5707" data-mp="2kyl4ng60at2h">
                            by {entry.user} · {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}